import { create } from "zustand";
import { persist } from "zustand/middleware";
import mixpanel from "mixpanel-browser";
import * as Sentry from "@sentry/react";
import { getProfile } from "./api";

export const useStore = create(
  persist(
    (set, get) => ({
      providers: [],
      setProviders: (providers) => set(() => ({ providers })),
      authToken: null,
      setAuthToken: (token) => set(() => ({ authToken: token })),
      savedSessions: [],
      profile: null,
      profileLoading: false,
      profileError: null,
      lastProfileFetch: null,
      fetchProfile: async () => {
        const { lastProfileFetch, profile, authToken } = get();
        if (!authToken) return; // Can't fetch profile without auth
        if (
          profile &&
          lastProfileFetch &&
          Date.now() - lastProfileFetch < 1000 * 60 * 2 // 2min
        ) {
          // Already cached
          return;
        }

        set({ profileLoading: true, profileError: null });
        try {
          const profileData = await getProfile();
          set({
            lastProfileFetch: Date.now(),
            profileLoading: false,
            profileError: null,
          });
          get().setProfile(profileData);
        } catch (e) {
          set({ profileLoading: false, profileError: e });
        }
      },

      setProfile: (profile) => {
        if (profile) {
          mixpanel.identify(profile.id);
          mixpanel.people.set({
            $name: `${profile.firstName} ${profile.lastName}`,
            $first_name: profile.firstName,
            $last_name: profile.lastName,
            $email: profile.emailAddress,
            $created: profile.created,
            $avatar: profile.profileImageUrl,
            plan: profile.activePlan,
            tenantId: profile.tenantId,
            role: profile.userRole,
          });
          Sentry.setUser({ userId: profile.id });
        } else {
          mixpanel.reset();
          Sentry.setUser(null);
        }
        set(() => ({ profile }));
        // Remove from savedSessions if this profile is now the active one
        if (profile) {
          const { savedSessions } = get();
          const filtered = savedSessions.filter(
            (s) => s.profile?.id !== profile.id
          );
          if (filtered.length !== savedSessions.length) {
            set({ savedSessions: filtered });
          }
        }
      },
      saveCurrentSession: () => {
        const { authToken, profile, savedSessions } = get();
        if (!authToken || !profile) return;
        const filtered = savedSessions.filter(
          (s) => s.profile?.id !== profile.id
        );
        filtered.push({ authToken, profile });
        set({ savedSessions: filtered, lastProfileFetch: null });
      },
      switchToSession: (profileId) => {
        const { authToken, profile, savedSessions } = get();
        const target = savedSessions.find((s) => s.profile?.id === profileId);
        if (!target) return;
        const newSaved = savedSessions.filter(
          (s) => s.profile?.id !== profileId
        );
        if (authToken && profile) {
          newSaved.push({ authToken, profile });
        }
        set({ savedSessions: newSaved, lastProfileFetch: null });
        get().setAuthToken(target.authToken);
        get().setProfile(target.profile);
      },
      removeSession: (profileId) => {
        const { savedSessions } = get();
        set({
          savedSessions: savedSessions.filter(
            (s) => s.profile?.id !== profileId
          ),
        });
      },
      chat: { selectedThread: null },
      setChat: (chat) => set(() => ({ chat })),
      threadListTimestamp: null,
      setThreadListTimestamp: (ts) => set(() => ({ threadListTimestamp: ts })),
      selectedPanelSession: null,
      setSelectedPanelSession: (session) =>
        set(() => ({ selectedPanelSession: session })),
      panelSessionListTimestamp: null,
      setPanelSessionListTimestamp: (ts) =>
        set(() => ({ panelSessionListTimestamp: ts })),
    }),
    {
      name: "lambrack", // TBD
      partialize: (state) => ({
        authToken: state.authToken,
        profile: state.profile,
        savedSessions: state.savedSessions,
      }),
    }
  )
);
